import { useState, useEffect, useMemo } from 'react'
import firebase from '../../firebase/compat'
import { client } from '../../hooks/Client'
import Location from '../../hooks/Location'
import { primaryTargetGroups, primaryTargetGroupLabels } from '../../helpers/dataHelpers/targetgroupCategories'
import PageHeader from '../../components/layout/PageHeader'
import SelectableGrid from '../../components/common/SelectableGrid'
import EffectDatabase from '../../components/meetstandaard/EffectDatabase'
import BenchmarkHeader, { DEFAULT_RESPONDENT_LABEL } from '../../components/benchmarkDetails/BenchmarkHeader'
import BenchmarkPositionBlock from '../../components/benchmarkDetails/BenchmarkPositionBlock'
import BenchmarkAdminTable from '../../components/benchmarkDetails/BenchmarkAdminTable'
import BenchmarkMSIMechanismsSection from '../../components/benchmarkDetails/BenchmarkMSIMechanismsSection'
import BenchmarkActivitiesSection from '../../components/benchmarkDetails/BenchmarkActivitiesSection'
import BenchmarkContextSection from '../../components/benchmarkDetails/BenchmarkContextSection'
import isSuperAdminTenant from '../../utils/superAdminTenant'
import { isBenchmarkStrongEnough } from '../../helpers/dataHelpers/getBenchmarkStrength'

const ALL_TARGETGROUPS = 'all'

const BenchmarksDetails = () => {
    const msiId = Location()[3]
    const effectDatabase = EffectDatabase()

    // SuperTenant sees the raw analysis (conclusions/activiteiten/context);
    // normal tenants see only the curated published insights for this effect's
    // sector.
    const isSuper = isSuperAdminTenant(client)

    const effect = effectDatabase
        ? effectDatabase.flatMap(category => category.effects || []).find(e => e.id === msiId)
        : null
    const effectName = (effect && effect.name) || ''

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [result, setResult] = useState(null)
    const [selectedTargetgroup, setSelectedTargetgroup] = useState(ALL_TARGETGROUPS)
    const [availableTargetgroups, setAvailableTargetgroups] = useState(null)

    const targetgroupItems = useMemo(() => {
        const base = [{ id: ALL_TARGETGROUPS, label: 'Alle doelgroepen' }]
        if (!availableTargetgroups) return base
        const filtered = primaryTargetGroups.filter(key => availableTargetgroups.includes(key))
        return [...base, ...filtered.map(key => ({ id: key, label: primaryTargetGroupLabels[key] }))]
    }, [availableTargetgroups])

    const subHeading = selectedTargetgroup === ALL_TARGETGROUPS
        ? effectName
        : `${effectName} voor ${primaryTargetGroupLabels[selectedTargetgroup]}`

    useEffect(() => {
        if (!msiId) return

        let cancelled = false
        const load = async () => {
            setLoading(true)
            setError('')
            setResult(null)

            const msiBenchmark = firebase.app().functions('europe-west1').httpsCallable('msiBenchmark')
            const response = await msiBenchmark({
                msiId,
                callerCompanyId: client,
                // explanation (top-vs-rest dimension analysis) is only rendered in
                // the superTenant sections — normal tenants shouldn't pay its extra
                // Firestore reads and payload on every view/filter switch.
                includeExplanation: isSuper,
                targetgroupLabel: selectedTargetgroup === ALL_TARGETGROUPS ? '' : selectedTargetgroup,
            })
            if (!cancelled) {
                const data = response.data || {}
                setResult(data)
                if (Array.isArray(data.availableTargetgroups)) {
                    setAvailableTargetgroups(data.availableTargetgroups)
                }
                setLoading(false)
            }
        }

        load().catch((err) => {
            console.error('BenchmarksDetails error:', err)
            if (!cancelled) {
                setError(err?.message || 'Gegevens konden niet worden geladen.')
                setLoading(false)
            }
        })

        return () => { cancelled = true }
    }, [msiId, selectedTargetgroup])

    return (
        <div className='main'>
            <div className='main-container'>
                <div className='benchmark-details-container'>

                    <PageHeader
                        heading={'Benchmark details'}
                        subHeading={subHeading}
                        description={'Hier zie je waar jullie staan ten opzichte van andere organisaties op dit effect.'}
                        AI={false}
                    />

                    <SelectableGrid
                        items={targetgroupItems}
                        getId={(item) => item.id}
                        getLabel={(item) => item.label}
                        selectedId={selectedTargetgroup}
                        onClick={setSelectedTargetgroup}
                    />
                    {loading && <p>Gegevens worden geladen…</p>}
                    {error && <p>{error}</p>}

                    {!loading && !error && result && result.benchmarkGrowth == null && (
                        <p className='benchmark-details-empty'>Er is nog onvoldoende data voor een benchmark op dit effect.</p>
                    )}

                    {/* Normal tenants only see the benchmark from 'Sterke benchmark'
                        strength up (the ScoreBlock entry point is hidden too, but the
                        URL stays reachable — and per-doelgroep slices can be weaker
                        than the overall benchmark). SuperTenant keeps the full view. */}
                    {!loading && !error && result && result.benchmarkGrowth != null && !isSuper && !isBenchmarkStrongEnough(result) && (
                        <p className='benchmark-details-empty'>Deze benchmark is nog niet sterk genoeg om te tonen. Zodra genoeg organisaties met voldoende respondenten meedoen, verschijnt hij hier.</p>
                    )}

                    {!loading && !error && result && result.benchmarkGrowth != null && (isSuper || isBenchmarkStrongEnough(result)) && (
                        <div className='benchmark-details-content'>
                            <BenchmarkHeader result={result} />

                            {result.viewMode === 'admin' ? (
                                <BenchmarkAdminTable
                                    positioning={result.positioning}
                                    benchmarkGrowth={result.benchmarkGrowth}
                                    respondentLabel={DEFAULT_RESPONDENT_LABEL}
                                />
                            ) : (
                                <BenchmarkPositionBlock
                                    positioning={result.positioning}
                                    benchmarkGrowth={result.benchmarkGrowth}
                                />
                            )}

                            {/* Het inzichtenblok is hier weg. Curatie gebeurt sinds de
                                Intelligence-feature op /intelligence: niet meer per
                                benchmarkpagina, maar onder de onderzoeksvraag die een
                                inzicht beantwoordt. Voor gewone organisaties komt er
                                een nieuwe weergave; die is een aparte feature.
                                AI-CONTEXT: See docs/features/intelligence-research-questions.md (§2b, §5)
                                De ruwe analysesecties hieronder blijven: dat is
                                analysemateriaal, geen curatie. */}
                            {isSuper && (
                                <>
                                    <BenchmarkMSIMechanismsSection
                                        explanation={result.explanation}
                                    />

                                    <BenchmarkActivitiesSection
                                        activitiesOverview={result.explanation?.activitiesOverview}
                                    />

                                    <BenchmarkContextSection
                                        explanation={result.explanation}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default BenchmarksDetails
